import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { useGetAccountServices, useGetPersonalInfo } from '@/hooks/api';
import { useGetMatchedRoute } from '@/hooks/routing';
import { groupServiceAccounts } from '@/routes/(services)/-model/group-service-accounts';
import { DocMultiple, Home, Shield, Tv } from '@nosinovacao/fe-icons';
import { cn, If, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type NavSheetContentSections } from '../../context/navbar';
import { NavItem } from './nav-item';

export function MainNavSheetContent({
  onClose,
  goToSection,
  className,
  currentSection,
}: Readonly<{
  onClose: () => void;
  goToSection: (section: Exclude<NavSheetContentSections, 'main'>) => void;
  className?: string;
  currentSection: NavSheetContentSections;
}>) {
  const { t } = useI18n();
  const match = useGetMatchedRoute();
  const { data, isLoading } = useGetPersonalInfo();
  const isTvSectionActive = currentSection === 'tv';
  const isServicesSectionActive = currentSection === 'services';
  return (
    <div className={cn('h-full overflow-y-auto', className)}>
      <ul className="grid pt-3">
        <NavItem
          active={
            match.routeId === '/(home)/home' && !isTvSectionActive && !isServicesSectionActive
          }
          route={{
            path: '/home',
            params: {},
            search: {},
          }}
          icon={Home}
          title={t('Common.Navbar.Routes.Home')}
          onClick={onClose}
          data-tid="nav-home"
        />
        <NavItem
          active={
            match.routeId === '/(security)/security' &&
            !isTvSectionActive &&
            !isServicesSectionActive
          }
          route={{
            path: '/security',
            params: {},
            search: {},
          }}
          icon={Shield}
          title={t('Common.Navbar.Routes.Security')}
          onClick={onClose}
          data-tid="nav-security"
        />
        <SkeletonLight loading={isLoading} className="h-[72px] w-full rounded-l-lg" count={2}>
          <NavItem
            active={
              (match.routeId === '/(services)/services' || isServicesSectionActive) &&
              !isTvSectionActive
            }
            route={
              data?.Type === 'Guest'
                ? undefined
                : {
                    path: '/services',
                    params: {},
                    search: {},
                  }
            }
            icon={DocMultiple}
            title={t('Common.Navbar.Routes.Services')}
            data-tid="nav-services"
            onClick={() => {
              if (data?.Type === 'Guest') {
                goToSection('services');
              } else onClose();
            }}
          />
          <If condition={!!data?.HasTvService}>
            <TvNavItem
              goToSection={goToSection}
              onClose={onClose}
              isTvSectionActive={isTvSectionActive}
              isServicesSectionActive={isServicesSectionActive}
            />
          </If>
        </SkeletonLight>
      </ul>
    </div>
  );
}

function TvNavItem({
  goToSection,
  isServicesSectionActive,
  isTvSectionActive,
  onClose,
}: Readonly<{
  isTvSectionActive: boolean;
  isServicesSectionActive: boolean;
  goToSection: (section: Exclude<NavSheetContentSections, 'main'>) => void;
  onClose: () => void;
}>) {
  const { t } = useI18n();
  const { data, isLoading } = useGetAccountServices();
  const serviceAccounts = groupServiceAccounts(data?.Holder?.ServiceAccounts ?? [])
    .filter((sa) => sa.HasTvService)
    .map((sa) => sa.Id);
  const match = useGetMatchedRoute();
  return (
    <SkeletonLight loading={isLoading} className="h-[72px]">
      <NavItem
        active={
          (match.routeId.startsWith('/(tv)/tv/') || isTvSectionActive) && !isServicesSectionActive
        }
        data-tid="nav-tv"
        route={
          serviceAccounts.length === 1
            ? {
                path: '/tv/$id',
                params: { id: serviceAccounts[0] },
                search: {},
              }
            : undefined
        }
        icon={Tv}
        title={t('Common.Navbar.Routes.Tv')}
        onClick={() => {
          if (serviceAccounts.length === 1) onClose();
          else goToSection('tv');
        }}
        canClickWhenActive
      />
    </SkeletonLight>
  );
}
